import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDown, ChevronUp, Loader2, TrendingUp } from 'lucide-react';
import { useSelector } from 'react-redux';

import { Badge } from '@client/components/ui/badge';
import { Button } from '@client/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CurrencyInput, PercentInput } from '@client/components/NumericInput';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import { selectClient } from '@client/model/selectors';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';

import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useSelfFundingForm } from '@/features/guided-financial-planning/hooks';

type SaveState = 'idle' | 'saving' | 'saved';

function getTaxRateLabel(assetTypeId: AssetTypeId): string {
  return assetTypeId === 'traditional-ira' ? 'INCOME TAX RATE' : 'CAPITAL GAINS TAX RATE';
}

function LoadingOverlay({ state }: { state: SaveState }) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <p className="text-sm font-medium text-gray-700">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

interface AccountCardProps {
  assetTypeId: AssetTypeId;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  fundingSourceId: string | null;
  existingSource?: SelfFundingSource;
  onSaveComplete: () => void;
  disabled?: boolean;
  allocationAmount?: number;
}

function isSourceComplete(source?: SelfFundingSource): boolean {
  if (!source) return false;
  return (
    source.existingAssetsValue !== null &&
    source.annualRateOfReturn !== null &&
    source.gainsTaxRate !== null
  );
}

export function AccountCard({
  assetTypeId,
  label,
  isExpanded,
  onToggle,
  fundingSourceId,
  existingSource,
  onSaveComplete,
  disabled,
  allocationAmount,
}: AccountCardProps) {
  const isComplete = isSourceComplete(existingSource);
  const { form, onSubmit, isSaving } = useSelfFundingForm(
    assetTypeId,
    fundingSourceId,
    existingSource,
  );
  const client = useSelector(selectClient);
  const watchedValues = useWatch({
    control: form.control,
    name: ['existingAssetsValue', 'yearlyContribution', 'annualRateOfReturn', 'gainsTaxRate'],
  });
  const [existingAssetsValue, yearlyContribution, annualRateOfReturn, gainsTaxRate] =
    watchedValues ?? [];

  const selfFundingStateForPreview = useMemo((): Partial<SelfFundingSource> | null => {
    if (existingAssetsValue == null || annualRateOfReturn == null || gainsTaxRate == null) {
      return null;
    }
    return {
      existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
      monthlyContributionAmount:
        yearlyContribution != null && typeof yearlyContribution === 'number'
          ? yearlyContribution / 12
          : null,
      annualRateOfReturn: typeof annualRateOfReturn === 'number' ? annualRateOfReturn : null,
      gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
    };
  }, [existingAssetsValue, yearlyContribution, annualRateOfReturn, gainsTaxRate]);

  const { ltcAtYear, yearsTillLtc, ltcAtAge } = client?.appliedInferenceSet ?? {};
  const clientBirthDate = client?.intakeSurvey?.clientBirthDate;

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingStateForPreview, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const { flags } = useFeatureFlags();
  const showPreview =
    flags.selfFundingGrowthPreview === true && projectedValue != null;

  const currentValueFormatted = formatCompactCurrency(
    typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
  );
  const previewTooltipCopy =
    'Current account value and projected value at predicted long-term care age, based on your growth and tax assumptions.';

  const previewBadgeContent = showPreview ? (
    <>
      <span className="text-gray-700">{currentValueFormatted}</span>
      <span className="mx-1"></span>
      <span className="inline-flex items-center font-semibold">
        <TrendingUp className="mr-1 size-3.5 shrink-0" />
        {formatCompactCurrency(projectedValue)}
      </span>
      <span className="ml-1 text-gray-700">(age {ageAtLtc ?? ltcAtYear})</span>
    </>
  ) : null;

  const isSessionComplete = useIsSessionComplete();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const saveCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track save state transitions
  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      saveCompleteTimeoutRef.current = setTimeout(() => {
        setSaveState('idle');
        onSaveComplete();
      }, 800);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, onSaveComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveCompleteTimeoutRef.current) {
        clearTimeout(saveCompleteTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{label}</span>
        </div>

        <div className="flex min-w-0 max-w-[65%] items-center justify-end gap-3">
          {showPreview && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex min-w-0 max-w-full shrink cursor-default">
                  <Badge
                    variant="secondary"
                    className="hidden min-w-0 max-w-full shrink truncate font-normal sm:inline-flex"
                  >
                    {previewBadgeContent}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs"
              >
                <p>{previewTooltipCopy}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {!showPreview && isComplete && existingSource?.existingAssetsValue != null && (
            <Badge
              variant="secondary"
              className="hidden font-medium sm:inline-flex"
            >
              {formatCurrency(existingSource.existingAssetsValue)}
              {isSessionComplete && allocationAmount && allocationAmount > 0 && (
                <span className="ml-1 text-gray-500">(−{formatCurrency(allocationAmount)})</span>
              )}
            </Badge>
          )}
          {flags.selfFundingGrowthPreview === true &&
            (showPreview || (isComplete && existingSource?.existingAssetsValue != null)) && (
            <Badge
              variant="secondary"
              className="font-medium sm:hidden"
            >
              {existingSource?.existingAssetsValue != null
                ? formatCurrency(existingSource.existingAssetsValue)
                : currentValueFormatted}
              {isSessionComplete &&
                allocationAmount &&
                allocationAmount > 0 &&
                existingSource?.existingAssetsValue != null && (
                  <span className="ml-1 text-gray-500">(−{formatCurrency(allocationAmount)})</span>
                )}
            </Badge>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="relative px-5 pb-5 pt-4">
              <LoadingOverlay state={saveState} />

              {showPreview && (
                <Badge
                  variant="secondary"
                  className="mb-4 w-fit font-normal sm:hidden"
                >
                  {ageAtLtc != null
                    ? `Projected value by age ${ageAtLtc} : `
                    : `Projected value by ${ltcAtYear} : `}
                  <span className="font-semibold">{formatCompactCurrency(projectedValue)}</span>
                </Badge>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Controller
                  name="existingAssetsValue"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <CurrencyInput
                      label="CURRENT VALUE"
                      placeholder="e.g. $50,000"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onPreview={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="yearlyContribution"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <CurrencyInput
                      label="FUTURE CONTRIBUTIONS (YEARLY)"
                      placeholder="e.g. $6,000"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onPreview={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="annualRateOfReturn"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <PercentInput
                      label="EXPECTED RATE OF RETURN"
                      placeholder="e.g. 7%"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onPreview={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="gainsTaxRate"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <PercentInput
                      label={getTaxRateLabel(assetTypeId)}
                      placeholder="e.g. 15%"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      onPreview={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              {!disabled && (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="mt-6 w-full rounded-full"
                >
                  Save
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
